import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme, alpha, lighten } from '@mui/material/styles';
import {
  Box, Typography, Button, TextField, Grid, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Paper,
  Tooltip, Avatar, Stack, MenuItem, FormControl, InputLabel,
  Select, Alert, Snackbar, InputAdornment, Collapse, Badge,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
  Event as EventIcon,
  Code as CodeIcon,
  Visibility as ViewIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import DataTable from '../components/common/DataTable';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { auditLogService } from '../services/auditLog';
import { adminService } from '../services/admin';
import { businessService } from '../services/business';
import { useLocation } from 'react-router-dom';

// ─── Debounce hook ────────────────────────────────────────────────────────────
function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
// Shorten any id to its last 8 chars for compact display
const shortId = (id) => (id ? `…${String(id).slice(-8)}` : null);

// Sentinel set in auditLogService — tells the UI this is an auth event row
const AUTH_EVENT_MARKER = 'auth-event@acs';

// ─── Action colour map ────────────────────────────────────────────────────────
const ACTION_COLORS = {
  CREATE:   { bg: '#dcfce7', color: '#166534', border: '#bbf7d0' },
  UPDATE:   { bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe' },
  DELETE:   { bg: '#fee2e2', color: '#991b1b', border: '#fecaca' },
  LOGIN:    { bg: '#fef9c3', color: '#854d0e', border: '#fde68a' },
  LOGOUT:   { bg: '#f3f4f6', color: '#374151', border: '#d1d5db' },
  PAYMENT:  { bg: '#ede9fe', color: '#5b21b6', border: '#ddd6fe' },
  BLOCK:    { bg: '#fee2e2', color: '#991b1b', border: '#fecaca' },
  UNBLOCK:  { bg: '#dcfce7', color: '#166534', border: '#bbf7d0' },
  DEFAULT:  { bg: '#f3f4f6', color: '#374151', border: '#d1d5db' },
};
// In dark mode, a pastel-on-white badge (designed for light backgrounds)
// reads as a stray light patch — swap it for a translucent tint of the
// same color with a lightened, readable foreground instead.
const chipStyleForMode = ({ color }, isDark) =>
  isDark ? { bg: alpha(color, 0.22), color: lighten(color, 0.5), border: alpha(color, 0.45) } : undefined;

const actionStyle = (action = '', isDark = false) => {
  const key = Object.keys(ACTION_COLORS).find(k => action.toUpperCase().includes(k));
  const base = ACTION_COLORS[key] || ACTION_COLORS.DEFAULT;
  return chipStyleForMode(base, isDark) || base;
};

// ─── Auth method chip styles ──────────────────────────────────────────────────
const METHOD_STYLES = {
  IN_APP: { bg: '#ede9fe', color: '#5b21b6', border: '#ddd6fe' },
  TOTP:   { bg: '#fff7ed', color: '#9a3412', border: '#fed7aa' },
};
const methodStyleForMode = (base, isDark) => chipStyleForMode(base, isDark) || base;

// ─── Component ────────────────────────────────────────────────────────────────
const AdminAuditLogs = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const location = useLocation();
  const isBusinessView = location.pathname.includes('/business');

  const [logs, setLogs]                   = useState([]);
  const [loading, setLoading]             = useState(true);
  const [filterOptions, setFilterOptions] = useState({ actions: [], targetTypes: [] });
  const [businesses, setBusinesses]       = useState([]);
  const [apps, setApps]                   = useState([]); // business view: this business's apps

  const [page, setPage]               = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRows, setTotalRows]     = useState(0);

  const [filters, setFilters] = useState({
    action:       '',
    targetType:   '',
    adminId:      '',
    businessName: '',
    businessAppId: '',
    subscriberId: '',
    phoneNumber:  '',
    startDate:    null,
    endDate:      null,
  });

  const [showFilters,   setShowFilters]   = useState(false);
  const [changeDialog,  setChangeDialog]  = useState({ open: false, title: '', data: null });
  const [snackbar,      setSnackbar]      = useState({ open: false, message: '', severity: 'success' });

  const debouncedAdminId      = useDebounce(filters.adminId,      500);
  const debouncedBusinessName = useDebounce(filters.businessName, 500);
  const debouncedSubscriberId = useDebounce(filters.subscriberId, 500);
  const debouncedPhoneNumber  = useDebounce(filters.phoneNumber,  500);

  useEffect(() => { fetchFilterOptions(); }, []);

  // Populate the Business filter dropdown with names. The selected name is
  // sent to the backend as-is (businessName param); the backend resolves
  // it to the matching business's businessId string internally — audit
  // rows key their business context by that id (see logBusinessAction's
  // contextId arg), not by name or UUID — so the lookup stays server-side.
  // Business view: load the logged-in business's own apps for the
  // Application dropdown (names shown, app id sent as businessAppId).
  useEffect(() => {
    if (!isBusinessView) return;
    businessService
      .getApplications()
      .then(setApps)
      .catch((err) => console.error('Failed to load applications for the audit log filter:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isBusinessView) return;
    adminService
      .getAllBusinesses(0, 200)
      .then((data) => {
        let list = [];
        if (Array.isArray(data)) list = data;
        else if (data.businesses && Array.isArray(data.businesses)) list = data.businesses;
        else if (data.data && Array.isArray(data.data)) list = data.data;
        setBusinesses(list);
      })
      .catch((err) => {
        console.error('Failed to load businesses for the audit log filter:', err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [
    page, rowsPerPage,
    filters.action, filters.targetType, filters.startDate, filters.endDate, filters.businessAppId,
    debouncedAdminId, debouncedBusinessName, debouncedSubscriberId, debouncedPhoneNumber,
  ]);

  const fetchFilterOptions = async () => {
    try {
      const res = await auditLogService.getFilters();
      setFilterOptions(res);
    } catch {
      showSnackbar('Failed to load filter options', 'error');
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {
        page:  page + 1,
        limit: rowsPerPage,
        ...(filters.action        && { action:       filters.action }),
        ...(filters.targetType    && { targetType:   filters.targetType }),
        ...(!isBusinessView && debouncedAdminId && { adminId: debouncedAdminId }),
        ...(isBusinessView && filters.businessAppId && { businessAppId: filters.businessAppId }),
        ...(debouncedBusinessName && { businessName: debouncedBusinessName }),
        ...(debouncedSubscriberId && { subscriberId: debouncedSubscriberId }),
        ...(debouncedPhoneNumber  && { phoneNumber:  debouncedPhoneNumber }),
      };
      // Send plain calendar dates (yyyy-MM-dd), not a full ISO timestamp.
      // A full ISO string carries the browser's local time-of-day, and
      // converting that through toISOString() shifts it into UTC — for any
      // timezone ahead of UTC (e.g. IST), midnight locally lands on the
      // *previous* UTC day, so the backend's UTC day-boundary filter would
      // silently exclude the very day the user picked. A bare date-only
      // string has no time/offset component, so it can't drift.
      if (filters.startDate instanceof Date && !isNaN(filters.startDate))
        params.startDate = format(filters.startDate, 'yyyy-MM-dd');
      if (filters.endDate instanceof Date && !isNaN(filters.endDate))
        params.endDate = format(filters.endDate, 'yyyy-MM-dd');

      const res     = await auditLogService.getLogs(params);
      const payload = res?.success !== undefined ? res : res?.data;
      setLogs(payload?.data ?? []);
      setTotalRows(payload?.total ?? 0);
    } catch {
      showSnackbar('Failed to fetch audit logs', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (msg, severity = 'success') =>
    setSnackbar({ open: true, message: msg, severity });

  const setFilter = (key, val) => {
    setFilters(prev => ({ ...prev, [key]: val }));
    setPage(0);
  };

  const clearAll = () => {
    setFilters({ action: '', targetType: '', adminId: '', businessName: '', businessAppId: '', subscriberId: '', phoneNumber: '', startDate: null, endDate: null });
    setPage(0);
  };

  const formatDate = (date) => {
    if (!date) return '—';
    try { return format(new Date(date), 'dd/MM/yy HH:mm'); } catch { return '—'; }
  };

  const activeChips = [
    filters.action        && { key: 'action',       label: `Action: ${filters.action}` },
    filters.targetType    && { key: 'targetType',    label: `Type: ${filters.targetType}` },
    filters.adminId       && { key: 'adminId',       label: `Admin: ${filters.adminId}` },
    filters.businessAppId && { key: 'businessAppId', label: `Application: ${apps.find(a => a.appId === filters.businessAppId)?.name || filters.businessAppId}` },
    filters.businessName  && { key: 'businessName',  label: `Business: ${filters.businessName}` },
    filters.subscriberId  && { key: 'subscriberId',  label: `Subscriber: ${filters.subscriberId}` },
    filters.phoneNumber   && { key: 'phoneNumber',   label: `Phone: ${filters.phoneNumber}` },
    filters.startDate     && { key: 'startDate',     label: `From: ${format(filters.startDate, 'dd/MM/yy')}` },
    filters.endDate       && { key: 'endDate',       label: `To: ${format(filters.endDate, 'dd/MM/yy')}` },
  ].filter(Boolean);

  // ─── Columns ───────────────────────────────────────────────────────────────
  const columns = [
    // ── Timestamp ────────────────────────────────────────────────────────────
    {
      field: 'timestamp',
      headerName: 'Time',
      width: 130,
      render: (v) => (
        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#374151', whiteSpace: 'nowrap' }}>
          {formatDate(v)}
        </Typography>
      ),
    },

    // ── Actor ─────────────────────────────────────────────────────────────────
    // Auth event rows  → show metadata.userId (external JWT id) in full
    // Admin portal rows → show real admin email + shortened admin_id
  // Actor column — add source chip below email/id
{
  field: 'actor',
  headerName: 'Actor',
  width: 120,
  render: (v, row) => {
    if (!row.source) return <Typography variant="caption" color="text.disabled">—</Typography>;

    const SOURCE_COLORS = {
      ADMIN_PORTAL: '#1e40af',
      BUSINESS:     '#166534',
      USER:         '#5b21b6',
      SYSTEM:       '#374151',
    };

    return (
      <Typography variant="caption" sx={{
        fontWeight: 700,
        fontSize: '0.72rem',
        color: SOURCE_COLORS[row.source] || '#374151',
      }}>
        {row.source}
      </Typography>
    );
  },
},
{
  field: 'actor_id',        // dummy field to avoid conflicts
  headerName: 'Actor ID',
  width: 130,
  render: (_, row) => {
    let id = null;

    if (row.source === 'USER') {
      id = row.metadata?.userId;
    } else if (row.source === 'BUSINESS') {
      id = row.target?.context_id || row.actor?.admin_id;
    } else if (row.source === 'ADMIN_PORTAL') {
      id = row.actor?.admin_id ? String(row.actor.admin_id) : null;
    }

    if (!id) return <Typography variant="caption" color="text.disabled">—</Typography>;

    return (
      <Tooltip title={String(id)} arrow>
        <Box
          onClick={() => navigator.clipboard.writeText(String(id))}
          sx={{
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            px: 0.75, py: 0.25,
            borderRadius: '5px',
            bgcolor: isDark ? alpha(theme.palette.common.white, 0.06) : '#f8fafc',
            border: `1px solid ${theme.palette.divider}`,
            '&:hover': { bgcolor: theme.palette.action.hover },
          }}
        >
          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.65rem', color: theme.palette.text.secondary }}>
            {String(id)}
          </Typography>
        </Box>
      </Tooltip>
    );
  },
},
    // ── Action ───────────────────────────────────────────────────────────────
    {
      field: 'action',
      headerName: 'Action',
      width: 155,
      render: (v) => {
        const s = actionStyle(v, isDark);
        return (
          <Chip label={v} size="small" sx={{
            backgroundColor: s.bg, color: s.color, border: `1px solid ${s.border}`,
            fontWeight: 700, fontSize: '0.65rem', height: 20, borderRadius: '6px',
          }} />
        );
      },
    },

    // ── Auth Method + Event Type ──────────────────────────────────────────────
    // Only meaningful for auth rows; shows "—" for admin-portal rows.
  {
  field: 'method',
  headerName: 'Method',
  width: 140,
  render: (_, row) => {          // ← use `row` instead of `v`
    const method = row.metadata?.authMethod;   // ← read from metadata

    if (!method) {
      return <Typography variant="caption" color="text.disabled">—</Typography>;
    }

    const ms = methodStyleForMode(METHOD_STYLES[method] || METHOD_STYLES.TOTP, isDark);

    return (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <Chip label={method} size="small" sx={{
          height: 18, fontSize: '0.6rem', fontWeight: 700,
          bgcolor: ms.bg, color: ms.color, border: `1px solid ${ms.border}`, borderRadius: '5px',
        }} />
      </Box>
    );
  },
},

    // ── Target ───────────────────────────────────────────────────────────────
    {
      field: 'target',
      headerName: 'Target',
      width: 160,
      render: (v) => (
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'block' }}>
            {v?.type || '—'}
          </Typography>
          {v?.id && (
            <Tooltip title={v.id}>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#9ca3af', fontSize: '0.65rem' }}>
                {v.id}
              </Typography>
            </Tooltip>
          )}
          {v?.context_id && (
            <Tooltip title={`Business ID: ${v.context_id}`}>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#c4b5fd', fontSize: '0.6rem', display: 'block' }}>
                {businesses.find(b => b.businessId === v.context_id)?.businessName || `biz ${v.context_id}`}
              </Typography>
            </Tooltip>
          )}
        </Box>
      ),
    },

    // ── Reason ───────────────────────────────────────────────────────────────
    {
      field: 'reason',
      headerName: 'Reason',
      width: 180,
      render: (v) => (
        <Typography variant="caption" sx={{ color: '#6b7280' }}>
          {v || '—'}
        </Typography>
      ),
    },

    // NOTE: "Changes" column hidden in the UI on purpose. The `changes` data is still
    // stored in the database and returned by the API; uncomment to show it again.
    // ── Changes ──────────────────────────────────────────────────────────────
//     {
//       field: 'changes',
//       headerName: 'Changes',
//       width: 130,
//       render: (v) => {
//         if (!v || Object.keys(v).length === 0)
//           return <Typography variant="caption" color="text.disabled">—</Typography>;
// 
//         // Auth events store flat data (no before/after shape)
//         if (!v.before && !v.after) {
//           return (
//             <Chip
//               label="Details"
//               size="small"
//               onClick={() => setChangeDialog({ open: true, title: 'Event Data', data: v })}
//               icon={<ViewIcon sx={{ fontSize: '12px !important' }} />}
//               sx={{
//                 height: 20, fontSize: '0.65rem',
//                 bgcolor: isDark ? alpha(theme.palette.common.white, 0.08) : '#f3f4f6',
//                 color: theme.palette.text.secondary,
//                 border: `1px solid ${theme.palette.divider}`,
//                 cursor: 'pointer', '&:hover': { bgcolor: theme.palette.action.hover },
//               }}
//             />
//           );
//         }
// 
//         return (
//           <Stack direction="row" spacing={0.5}>
//             {v.before && (
//               <Chip label="Before" size="small"
//                 onClick={() => setChangeDialog({ open: true, title: 'Before State', data: v.before })}
//                 icon={<ViewIcon sx={{ fontSize: '12px !important' }} />}
//                 sx={{
//                   height: 20, fontSize: '0.65rem',
//                   bgcolor: isDark ? alpha(theme.palette.common.white, 0.08) : '#f3f4f6',
//                   color: theme.palette.text.secondary,
//                   border: `1px solid ${theme.palette.divider}`,
//                   cursor: 'pointer', '&:hover': { bgcolor: theme.palette.action.hover },
//                 }} />
//             )}
//             {v.after && (
//               <Chip label="After" size="small"
//                 onClick={() => setChangeDialog({ open: true, title: 'After State', data: v.after })}
//                 icon={<CodeIcon sx={{ fontSize: '12px !important' }} />}
//                 sx={{
//                   height: 20, fontSize: '0.65rem',
//                   bgcolor: alpha(theme.palette.primary.main, isDark ? 0.22 : 0.1),
//                   color: isDark ? lighten(theme.palette.primary.main, 0.4) : theme.palette.primary.dark,
//                   border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.45 : 0.3)}`,
//                   cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.primary.main, isDark ? 0.32 : 0.18) },
//                 }} />
//             )}
//           </Stack>
//         );
//       },
//     },

    // ── Metadata tooltip ──────────────────────────────────────────────────────
    {
      field: 'metadata',
      headerName: 'Meta',
      width: 70,
      render: (v) => {
        if (!v || Object.keys(v).length === 0)
          return <Typography variant="caption" color="text.disabled">—</Typography>;
        return (
          <Tooltip title={<pre style={{ margin: 0, fontSize: 11 }}>{JSON.stringify(v, null, 2)}</pre>} arrow>
            <Chip label="···" size="small" sx={{
              height: 20, fontSize: '0.7rem',
              bgcolor: isDark ? alpha(theme.palette.common.white, 0.08) : '#f3f4f6',
              color: theme.palette.text.secondary, cursor: 'default', border: `1px solid ${theme.palette.divider}`,
            }} />
          </Tooltip>
        );
      },
    },
  ];

  if (loading && page === 0 && logs.length === 0) return <LoadingSpinner />;

  return (
    <Box sx={{
      height: 'calc(100vh - 64px)',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: theme.palette.background.default,
      pt: 2, px: 2, pb: 2,
      gap: 1.5,
      overflow: 'hidden',
      boxSizing: 'border-box',
    }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{
          width: 34, height: 34, borderRadius: '10px',
          background: '#2563EB',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {isBusinessView
            ? <BusinessIcon sx={{ color: '#fff', fontSize: 18 }} />
            : <AdminIcon    sx={{ color: '#fff', fontSize: 18 }} />
          }
        </Box>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.2 }}>
            {isBusinessView ? 'Business Audit Logs' : 'Admin Audit Logs'}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {totalRows.toLocaleString()} records
            {activeChips.length > 0 ? ` · ${activeChips.length} filter${activeChips.length > 1 ? 's' : ''} active` : ''}
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Badge badgeContent={activeChips.length} color="primary" invisible={activeChips.length === 0}>
            <Button
              size="small"
              variant={showFilters ? 'contained' : 'outlined'}
              startIcon={<FilterIcon />}
              endIcon={showFilters ? <ArrowUpIcon /> : <ArrowDownIcon />}
              onClick={() => setShowFilters(v => !v)}
              sx={{
                textTransform: 'none', fontWeight: 600, borderRadius: '8px', fontSize: '0.8rem',
                ...(showFilters
                  ? { background: theme.palette.primary.dark, color: '#fff', '&:hover': { background: theme.palette.primary.main } }
                  : { borderColor: theme.palette.divider, color: theme.palette.text.secondary, '&:hover': { borderColor: theme.palette.text.secondary, bgcolor: theme.palette.action.hover } }
                ),
              }}
            >
              Filters
            </Button>
          </Badge>
          {activeChips.length > 0 && (
            <Button size="small" onClick={clearAll} startIcon={<ClearIcon />}
              sx={{ textTransform: 'none', color: '#ef4444', fontWeight: 600, fontSize: '0.8rem', borderRadius: '8px', '&:hover': { bgcolor: '#fef2f2' } }}>
              Clear all
            </Button>
          )}
        </Box>
      </Box>

      {/* ── Filter Panel ───────────────────────────────────────────────────── */}
      <Collapse in={showFilters}>
 <Paper
  elevation={0}
  sx={{
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '12px',
    p: 3,
    bgcolor: theme.palette.background.paper,
  }}
>
  <Grid container spacing={2} alignItems="flex-end">
    
    {/* Action Filter */}
    <Grid item xs={12} sm={6} md={4}>
      <FormControl fullWidth size="small">
        <Select
          value={filters.action}
          onChange={e => setFilter('action', e.target.value)}
          displayEmpty
          renderValue={(selected) => {
            if (selected === '') {
              return <em style={{ color: '#6b7280' }}>Action</em>;
            }
            return selected;
          }}
          sx={{
            borderRadius: '8px',
            fontSize: '0.8rem',
            '& .MuiSelect-select': { py: 1.2 },
          }}
        >
          <MenuItem value=""><em>All</em></MenuItem>
          {filterOptions.actions.map(a => (
            <MenuItem key={a} value={a} sx={{ fontSize: '0.8rem' }}>{a}</MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>

    {/* Target Type Filter */}
    <Grid item xs={12} sm={6} md={4}>
      <FormControl fullWidth size="small">
        <Select
          value={filters.targetType}
          onChange={e => setFilter('targetType', e.target.value)}
          displayEmpty
          renderValue={(selected) => {
            if (selected === '') {
              return <em style={{ color: '#6b7280' }}>Target Type</em>;
            }
            return selected;
          }}
          sx={{
            borderRadius: '8px',
            fontSize: '0.8rem',
            '& .MuiSelect-select': { py: 1.2 },
          }}
        >
          <MenuItem value=""><em>All</em></MenuItem>
          {filterOptions.targetTypes.map(t => (
            <MenuItem key={t} value={t} sx={{ fontSize: '0.8rem' }}>{t}</MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>

    {!isBusinessView && (
    <>
    {/* ✅ NEW: Admin ID Filter */}
    <Grid item xs={12} sm={6} md={4}>
      <TextField
        fullWidth
        size="small"
        label="Admin ID"
        value={filters.adminId}
        onChange={e => setFilter('adminId', e.target.value)}
        placeholder="UUID"
        sx={{
          '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.8rem' },
          '& label': { fontSize: '0.8rem' }
        }}
      />
    </Grid>

    </>
    )}

    {isBusinessView ? (
    <>
    {/* Application Filter (business view) — shows app names; the selected
        app's id is what gets sent as businessAppId */}
    <Grid item xs={12} sm={6} md={4}>
      <FormControl fullWidth size="small">
        <InputLabel sx={{ fontSize: '0.8rem' }}>Application</InputLabel>
        <Select
          label="Application"
          value={filters.businessAppId}
          onChange={e => setFilter('businessAppId', e.target.value)}
          sx={{
            borderRadius: '8px',
            fontSize: '0.8rem',
            '& .MuiSelect-select': { py: 1.2 },
          }}
        >
          <MenuItem value=""><em>All Applications</em></MenuItem>
          {apps.map(a => (
            <MenuItem key={a.appId} value={a.appId} sx={{ fontSize: '0.8rem' }}>
              {a.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>

    </>
    ) : (
    <>
    {/* Business Filter — dropdown of business names; the selected name is
        sent to the backend as-is, which resolves it to the matching
        business's id server-side to actually filter the audit rows */}
    <Grid item xs={12} sm={6} md={4}>
      <FormControl fullWidth size="small">
        <InputLabel sx={{ fontSize: '0.8rem' }}>Business</InputLabel>
        <Select
          label="Business"
          value={filters.businessName}
          onChange={e => setFilter('businessName', e.target.value)}
          sx={{
            borderRadius: '8px',
            fontSize: '0.8rem',
            '& .MuiSelect-select': { py: 1.2 },
          }}
        >
          <MenuItem value=""><em>All Businesses</em></MenuItem>
          {businesses.map(b => (
            <MenuItem key={b._id || b.businessId} value={b.businessName} sx={{ fontSize: '0.8rem' }}>
              {b.businessName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>

    </>
    )}

    {/* ✅ NEW: Subscriber ID Filter */}
    <Grid item xs={12} sm={6} md={4}>
      <TextField
        fullWidth
        size="small"
        label="Subscriber ID"
        value={filters.subscriberId}
        onChange={e => setFilter('subscriberId', e.target.value)}
        placeholder="Subscriber UUID"
        sx={{
          '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.8rem' },
          '& label': { fontSize: '0.8rem' }
        }}
      />
    </Grid>

    {/* Phone Number Filter */}
    <Grid item xs={12} sm={6} md={4}>
      <TextField
        fullWidth
        size="small"
        label="Phone Number"
        value={filters.phoneNumber}
        onChange={e => setFilter('phoneNumber', e.target.value)}
        placeholder="e.g. 9198765432"
        sx={{
          '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.8rem' },
          '& label': { fontSize: '0.8rem' }
        }}
      />
    </Grid>

    {/* Date filters — "To" can never be before "From" and vice versa, and
        neither can go past today */}
    <Grid item xs={6} sm={4} md={2}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          label="From"
          value={filters.startDate}
          onChange={d => setFilter('startDate', d)}
          maxDate={filters.endDate || undefined}
          disableFuture
          slotProps={{
            textField: {
              size: 'small',
              fullWidth: true,
              sx: {
                '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.8rem' },
                '& label': { fontSize: '0.8rem' }
              }
            }
          }}
        />
      </LocalizationProvider>
    </Grid>
    <Grid item xs={6} sm={4} md={2}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          label="To"
          value={filters.endDate}
          onChange={d => setFilter('endDate', d)}
          minDate={filters.startDate || undefined}
          disableFuture
          slotProps={{
            textField: {
              size: 'small',
              fullWidth: true,
              sx: {
                '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.8rem' },
                '& label': { fontSize: '0.8rem' }
              }
            }
          }}
        />
      </LocalizationProvider>
    </Grid>
  </Grid>

  {/* Active chips */}
  {activeChips.length > 0 && (
    <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {activeChips.map(chip => (
        <Chip key={chip.key} label={chip.label} size="small"
          onDelete={() => setFilter(chip.key, chip.key.includes('Date') ? null : '')}
          sx={{
            height: 22, fontSize: '0.7rem', fontWeight: 600,
            bgcolor: alpha(theme.palette.primary.main, isDark ? 0.22 : 0.1),
            color: isDark ? lighten(theme.palette.primary.main, 0.4) : theme.palette.primary.dark,
            border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.45 : 0.3)}`,
            '& .MuiChip-deleteIcon': { fontSize: 14, color: theme.palette.primary.main, '&:hover': { color: theme.palette.primary.dark } },
          }}
        />
      ))}
    </Box>
  )}
</Paper>
      </Collapse>

      {/* ── Table ─────────────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '12px',
        background: theme.palette.background.paper,
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        '& .MuiTableContainer-root': { flex: 1, minHeight: 0, overflow: 'auto' },
        '& .MuiTablePagination-root': { flexShrink: 0, borderTop: `1px solid ${theme.palette.divider}` },
      }}>
        <DataTable
          columns={columns}
          data={logs}
          page={page}
          rowsPerPage={rowsPerPage}
          totalRows={totalRows}
          onPageChange={setPage}
          onRowsPerPageChange={(n) => { setRowsPerPage(n); setPage(0); }}
          loading={loading}
          sx={{
            flex: 1,
            background: theme.palette.background.paper,
            '& .MuiTableHead-root .MuiTableCell-head': {
              bgcolor: isDark ? alpha(theme.palette.common.white, 0.04) : '#f9fafb',
              fontWeight: 700, color: theme.palette.text.secondary,
              fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em',
              borderBottom: `2px solid ${theme.palette.divider}`, py: 1, whiteSpace: 'nowrap',
            },
            '& .MuiTableBody-root .MuiTableRow-root': {
              '&:hover': { bgcolor: isDark ? alpha(theme.palette.primary.main, 0.08) : '#f0f9ff' },
              transition: 'background 0.15s',
            },
            '& .MuiTableBody-root .MuiTableCell-root': {
              py: 0.75, borderBottom: `1px solid ${theme.palette.divider}`, fontSize: '0.8rem', color: theme.palette.text.primary,
            },
          }}
        />
      </Paper>

      {/* ── Change / Detail Dialog ─────────────────────────────────────────── */}
      <Dialog open={changeDialog.open} onClose={() => setChangeDialog({ open: false, title: '', data: null })}
        maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ bgcolor: isDark ? alpha(theme.palette.common.white, 0.04) : '#f9fafb', fontWeight: 700, color: theme.palette.text.primary, borderBottom: `1px solid ${theme.palette.divider}`, py: 1.5, fontSize: '0.95rem' }}>
          {changeDialog.title}
        </DialogTitle>
        <DialogContent sx={{ p: 2, bgcolor: isDark ? theme.palette.background.default : '#f1f5f9' }}>
          <Box sx={{ bgcolor: '#0f172a', color: '#e2e8f0', p: 2, borderRadius: '8px', fontFamily: '"Fira Code","Consolas",monospace', fontSize: '0.8rem', overflow: 'auto', maxHeight: 420 }}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {JSON.stringify(changeDialog.data, null, 2)}
            </pre>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 1.5, bgcolor: isDark ? alpha(theme.palette.common.white, 0.04) : '#f9fafb' }}>
          <Button size="small" variant="contained" onClick={() => setChangeDialog({ open: false, title: '', data: null })}
            sx={{ background: theme.palette.primary.dark, borderRadius: '8px', textTransform: 'none', '&:hover': { background: theme.palette.primary.main } }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ──────────────────────────────────────────────────────── */}
      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: '8px', fontWeight: 500 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminAuditLogs;

